// src/utils/clientCareTurnIn.js
// A Team Lead's own Sale Turn-In also waits for Client Care.
//
// Henry, 2026-09-19: "when a team lead turns in a file, even though they go through the entire process, client
// care should still see it so they can process it their own way", and it leaves the list "once they process it
// and approve. its only for approvals." A Team Lead's turn-in is saved Approved the moment it is submitted (the
// Team Lead filled in the commission), so it never reached the Pending Approval list. He chose: it still counts
// right away everywhere (cap panel, reports, Deals Board), AND it sits in Pending Approval until Client Care
// clicks Review & Approve. Only turn-ins made from now on; older ones are not added.
//
// Pure: no React, no Firestore. Spec: docs/superpowers/specs/2026-09-19-team-lead-turnins-client-care-design.md.

// The Team Lead role, as stored ("Admin" is shown as Team Lead; see roles-and-ui-labels).
export const TEAM_LEAD_ROLE = "Admin";

// The extra fields a NEW sale turn-in gets when a Team Lead submits it already approved. Anyone else (an Agent,
// whose turn-in is Pending anyway; Client Care or the Accountant, who process their own) gets nothing extra.
export function clientCareFieldsForNewTurnIn({ submitter, status, requestType, now } = {}) {
  if (!submitter || submitter.role !== TEAM_LEAD_ROLE) return {};
  if (status !== "Approved" || requestType !== "saleTurnIn") return {};
  return { awaitingClientCare: true, awaitingClientCareSince: Number(now) || Date.now() };
}

// The same fields when a Team Lead APPROVES someone else's turn-in (Henry, 2026-09-19: "this should always show
// files even if I have approved them ... that way client care advisors are able to look at anything"). An agent's
// turn-in that a Team Lead reviews and approves used to leave the list for good; now it waits for Client Care in
// exactly the same way. Client Care approving it is the processing itself, so their save flags nothing, and the
// Accountant's does not either. Only the Pending to Approved flip counts: a later edit of an approved file is not
// an approval.
export function clientCareFieldsForApproval({ approver, wasStatus, nextStatus, requestType, now } = {}) {
  if (!approver || approver.role !== TEAM_LEAD_ROLE) return {};
  if (wasStatus !== "Pending" || nextStatus !== "Approved" || requestType !== "saleTurnIn") return {};
  return { awaitingClientCare: true, awaitingClientCareSince: Number(now) || Date.now() };
}

// Only the exact value true counts, so a stray string or an old field can never put a file on the list.
export function isAwaitingClientCare(deal) {
  return Boolean(deal) && deal.awaitingClientCare === true;
}

// Is this deal on the Pending Approval list? An agent's turn-in waiting for approval, as before, or a Team
// Lead's approved turn-in that Client Care has not processed yet.
export function isInApprovalQueue(deal) {
  if (!deal || deal.requestType !== "saleTurnIn") return false;
  return deal.status === "Pending" || isAwaitingClientCare(deal);
}

// The Pending Approval list itself, newest first: what the dashboard card and the full page
// (pages/approvals.jsx) both show, built in one place so the two can never drift apart.
// Archived deals are left out: the deals list still carries them (only soft-deleted ones are
// dropped), and a file taken off the Deals Board as a duplicate must not keep a seat here,
// where the only way off the list would be to approve a dead deal. Restore brings the row back.
export function selectApprovalQueue(deals) {
  return (deals || [])
    .filter((d) => d?.archived !== true && isInApprovalQueue(d))
    .sort((a, b) => Number(b?.createdat || 0) - Number(a?.createdat || 0));
}

// What Review & Approve writes when Client Care finishes a Team Lead's turn-in: off the list, with who and when.
export function clientCareProcessedPatch({ user, now } = {}) {
  const by = (user && (user.uid || user.id)) || "";
  return {
    awaitingClientCare: false,
    clientCareProcessedAt: Number(now) || Date.now(),
    clientCareProcessedBy: String(by),
  };
}
